/**
 * @fileoverview usePersistedSet — a `Set<string>` backed by localStorage.
 * @module utils/use-persisted-set
 *
 * Several overview panels (the Estimated Outcome waterfall, the Assets /
 * Security Interests / Creditors tables) track which sub-category groups are
 * expanded as a `Set<string>` with an "Expand all / Collapse all" control.
 * MOR-626 wants those sub-categories collapsed by default but the user's
 * expand/collapse choice remembered across reloads.
 *
 * This hook is a drop-in replacement for `useState<Set<string>>` that persists
 * the set globally under `key` (no jobId — the choice is a user preference that
 * carries across jobs and across the overview / detail surfaces). The set is
 * stored as a JSON array. Mirrors the read-in-initializer / write-in-effect
 * pattern used by the theme switcher (`src/ui/settings/theme-switcher.tsx`).
 */

import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

export function usePersistedSet(
  key: string,
  fallback: Iterable<string> = [],
): [Set<string>, Dispatch<SetStateAction<Set<string>>>] {
  const [value, setValue] = useState<Set<string>>(() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem(key);
      if (stored) {
        try {
          const parsed: unknown = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            return new Set(
              parsed.filter((x): x is string => typeof x === "string"),
            );
          }
        } catch {
          // Corrupt value — fall through to the default.
        }
      }
    }
    return new Set(fallback);
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, JSON.stringify([...value]));
  }, [key, value]);

  return [value, setValue];
}
